import { StackedAvatars } from "@/components/ui/stacked-avatars";
import { IconButton } from "@somethings-ui/web";
import { Pencil } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { HubNameModal } from "./hub-name-modal";
import { MentorshipDetails } from "@/lib/api/mentorships";
import { useAbility } from "@/lib/casl/ability-context";
import {
  getMenteeDisplayName,
  getMenteeDisplayPhoto,
} from "@/lib/redacted-display";

interface MentorshipCardProps {
  mentorship: MentorshipDetails;
}

export function MentorshipCard({ mentorship }: MentorshipCardProps) {
  const ability = useAbility();
  const canUpdateMentorships = ability.can("update", "mentorships");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const formattedDate = mentorship.created_at
    ? DateTime.fromJSDate(new Date(mentorship.created_at)).toFormat("MMMM yyyy")
    : null;

  const menteeDisplayName = getMenteeDisplayName(mentorship.mentee);
  const menteeDisplayPhoto = getMenteeDisplayPhoto(mentorship.mentee);

  const currentName =
    mentorship.metadata?.customName ||
    `${menteeDisplayName} & ${mentorship.mentor.name ?? ""}`;

  return (
    <div className="flex items-center gap-3 py-4">
      <StackedAvatars
        avatars={[
          {
            imageUrl: menteeDisplayPhoto,
            name: menteeDisplayName,
          },
          {
            imageUrl: mentorship.mentor.profile_picture_url ?? undefined,
            name: mentorship.mentor.name ?? "",
          },
        ]}
        size="lg"
        showBorder={true}
        reverse
      />

      <div className="flex flex-1 flex-col">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold">{currentName}</span>
          {canUpdateMentorships && (
            <IconButton
              variant="ghost-black"
              onClick={() => setIsModalOpen(true)}
              icon={<Pencil className="h-4 w-4" fill="currentColor" />}
            />
          )}
        </div>
        {formattedDate && (
          <span className="text-sm text-gray-500">Since {formattedDate}</span>
        )}
      </div>
      {canUpdateMentorships && (
        <HubNameModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          mentorship={mentorship}
        />
      )}
    </div>
  );
}
